import {
  AnalyticsActionFavoritesAddEvent,
  AnalyticsActionFavoritesRemoveEvent,
  AnalyticsEventParam,
} from '@accedo/one-plugin-sdk';
import { mutate } from 'swr';
import { proxy, useSnapshot } from 'valtio';
import { subscribeKey } from 'valtio/utils';

import { favoritesService } from '#/client/api';
import { FAVORITES_CACHE_KEY } from '#/client/constants';
import { MediaType } from '#/client/models';
import { devtoolsApi } from '#/dev/devtools';
import { ignoreError } from '#/service/utils';

import { authStore } from '../auth/auth.store';
import { addToFavorites, removeFromFavorites } from './favorites.api';

interface FavoritesStore {
  favoriteIds: string[];
}

export const favoritesStore = proxy<FavoritesStore>({
  favoriteIds: [],
});

export const favoritesActions = {
  async load() {
    try {
      const { data } = await favoritesService.getAll();
      const ids = (data.data || []).map((item) => item.objectId);
      favoritesStore.favoriteIds = ids;
    } catch (e) {
      console.warn('Loading user favorites failed', e);
      favoritesActions.reset();
    }
  },

  /**
   * Adds an item to the user's list. Resolves to whether it actually landed,
   * so the caller can decide about confirmation UI.
   *
   * The store is updated optimistically and rolled back on failure. Everything
   * that draws a favourites toggle reads its state from `favoriteIds`, and
   * `addToFavorites` is two sequential requests (a catalog fetch, then the
   * mutation) - waiting for them before touching the store left the button
   * showing the pre-click state for seconds, with no sign the click registered.
   */
  async addFavorite(itemId: string, mediaType: MediaType): Promise<boolean> {
    if (favoritesStore.favoriteIds.includes(itemId)) {
      return false;
    }

    favoritesStore.favoriteIds.push(itemId);

    try {
      // call api
      const mediaResource = await addToFavorites(itemId, mediaType);

      // #ExternalAnalytics - Add to favorites
      document.dispatchEvent(
        new AnalyticsActionFavoritesAddEvent({
          [AnalyticsEventParam.ContentId]: itemId,
          [AnalyticsEventParam.Title]: mediaResource.title,
        })
      );

      return true;
    } catch (err) {
      console.warn(err);
      // Filtered rather than restored from a snapshot, so a favourite added
      // elsewhere while this request was in flight survives the rollback.
      favoritesStore.favoriteIds = favoritesStore.favoriteIds.filter(
        (id) => id !== itemId
      );

      return false;
    }
  },

  /** Mirror of `addFavorite`: optimistic, rolled back on failure. */
  async removeFavorite(
    itemId: string,
    mediaType: MediaType
  ): Promise<boolean> {
    if (!favoritesStore.favoriteIds.includes(itemId)) {
      return false;
    }

    favoritesStore.favoriteIds = favoritesStore.favoriteIds.filter(
      (id) => id !== itemId
    );

    try {
      // call api
      const mediaResource = await removeFromFavorites(itemId, mediaType);

      // #ExternalAnalytics - Remove from favorites
      document.dispatchEvent(
        new AnalyticsActionFavoritesRemoveEvent({
          [AnalyticsEventParam.ContentId]: itemId,
          [AnalyticsEventParam.Title]: mediaResource.title,
        })
      );

      return true;
    } catch (err) {
      console.warn(err);

      if (!favoritesStore.favoriteIds.includes(itemId)) {
        favoritesStore.favoriteIds.push(itemId);
      }

      return false;
    }
  },

  reset() {
    favoritesStore.favoriteIds = [];
  },
};

/**
 * Subscribing to the auth store to handle following cases:
 *
 * 1. Load all favorites on app start to be able to handle favorites in both unauthenticated and authenticated states.
 * 2. Watch for changes in the logged in state and re-fetch favorites to keep the favorites in sync with the user's state.
 *
 * Beware that both of these will be batched and only trigger one re-render. If you ever want to build something that should
 * trigger a re-render on every change, you can leverage the notifyInSync property to disabled batching of subscriptions.
 */

subscribeKey(authStore, 'isInitialised', () => {
  favoritesActions.load().catch(ignoreError);
});

subscribeKey(authStore, 'isLoggedIn', () => {
  favoritesActions.load().catch(ignoreError);
  mutate(FAVORITES_CACHE_KEY).catch(ignoreError);
});

export const useFavoritesStore = () =>
  useSnapshot(favoritesStore) as FavoritesStore;

devtoolsApi.setStore('favorites', favoritesStore, favoritesActions);
